import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import Loading from "Components/Loading";
import DeleteAccountButton from "Pages/MyAccounts/DeleteAccountButton";
import useSession from "Utils/Hooks/useSession";
import errorIcon from "Assets/Icons/warning-2.svg";

import "./BankList.scss";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import useTransactionInitiated from "Utils/Hooks/useTransactionInitiated";
// eslint-disable-next-line no-unused-vars
import _, { set } from "lodash";
import { FUND_SOURCE_TYPE, PRODUCTTYPES, Routes } from "Utils/Constants";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import { faro } from "@grafana/faro-react";
import { Box, Button } from "@pav/pav-products-design-system";
import { Radio } from "@mui/material";
import useView, { VIEW } from "Utils/Hooks/useView";

const BankList = () => {
  const fundType = useFundType();
  const { session } = useSession();
  const mode = useMode();
  const view = useView();
  const isCashier = view === VIEW.cashier;
  const [fundingSources, setFundingSources] = useState(undefined);
  const [bankAccountList, setBankAccountList] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [userSelection, setUserSelection] = useState(null);
  const vipApi = useVipApiFetcher();
  const history = useHistory();
  const { transactionInitiated } = useTransactionInitiated();
  const isDepositMode = mode === MODE.deposit;
  const [sessionStorageTransAmount, setSessionStorageTransAmount] =
    useSessionStorage("vip-transaction-amount");

  const customButtons = [
    {
      label: "Done",
      route: undefined,
      returnToOperator: true,
      returnUrl: session?.returnUrl,
      transactionId: session?.transactionId,
      transactionType: "bankManagement",
    },
  ];

  const customButtonsComponents = customButtons?.map((customButton, index) => {
    return (
      <Box sx={{ width: "100%", margin: "var(--spacing-lg) 0" }}>
        {isCashier
          ? null
          : session.returnUrl && (
              <Button
                fullWidth
                buttonType="secondary"
                className={
                  customButton.zeroBal || customButton.label === "CANCEL"
                    ? "click-cancel"
                    : "submit-button click-return-to-opr"
                }
                onClick={() => {
                  customButton.returnToOperator
                    ? WcRedirect(customButton)
                    : history.push({
                        pathname: customButton.route,
                        state: { notRedirect: customButton.notRedirect },
                      });
                }}
              >
                {customButton.label}
              </Button>
            )}
      </Box>
    );
  });

  function WcRedirect(customButton) {
    if (customButton.returnUrl) {
      var queryStringSeperator = customButton.returnUrl.includes("?")
        ? "&"
        : "?";
      window.location.replace(
        customButton.returnUrl +
          queryStringSeperator +
          "transactionId=" +
          customButton.transactionId +
          "&transactionType=" +
          customButton.transactionType,
      );
    } else {
      if (window.opener !== null && !window.opener.closed) {
        window.close();
      } else if (
        window.parent !== null &&
        window.parent.frames !== null &&
        window.parent.frames.length > 0
      ) {
        window.parent.postMessage("close", "*");
      } else {
        if (/android/i.test(navigator.userAgent)) {
          if (typeof window !== undefined) {
            window.postMessage("close", "*");
          } else if (document !== undefined) {
            document.postMessage("close", "*");
          }
        } else if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          window.postMessage("close", "*");
        }
      }
    }
    invalidateSession();
    setVipAccount(null);
  }

  useEffect(() => {
    if (!vipApi.ready) return;
    let mounted = true;

    (async () => {
      let fundList = [];
      let plaidFundList = [];

      fundList = await vipApi.fetchData("/api/bank/ach");
      setBankAccountList(fundList);
      setFundingSources(fundList);
      if (session?.patronProductType === PRODUCTTYPES.ONLINE) {
        plaidFundList = fundList.filter(
          (fund) =>
            fund.achOpenType === FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
            fund.achOpenType === FUND_SOURCE_TYPE.FINICITY,
        ); // Get only Plaid accounts
        setFundingSources(plaidFundList);
      }

      if (!mounted) return;

      setIsLoading(false);
    })();
    return () => {
      mounted = false;
    }; // Unmount func
  }, [vipApi.ready]);

  if (isLoading) return <Loading />;

  async function updateAccount(account) {
    if (account) {
      faro.api.pushEvent(
        `User set default bank: ${account.maskedAccountNumber}`,
      );
      const body = {
        bankName: account.bankName,
        routingNumber: account.routingNumber,
        accountNumber: account.accountNumber,
        maskedAccountNumber: account.maskedAccountNumber,
        achOpenType: account.achOpenType,
        ipAddress: window.sessionStorage.getItem("clientIpAddress"),
      };
      const customErrorButtons =
        mode === MODE.manageBanks && transactionInitiated
          ? [
              {
                label: "Try Again",
                route: Routes.MY_ACCOUNTS,
              },
            ]
          : transactionInitiated
            ? [
                {
                  label: "Try Again",
                  route: Routes.MY_ACCOUNTS,
                },
                {
                  label: `Complete ${isDepositMode ? "Deposit" : "Withraw"}`,
                  route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
                },
              ]
            : undefined;
      const response = await vipApi.fetch("/api/bank/update", "POST", body);
      if (response) {
        switch (response.status) {
          case 200:
            setUserSelection(account.accountNumber);
            break;
          case 401:
            console.error(response);
            faro.api.pushError(new Error("Bank Update 401 Error"), {
              context: response,
            });
            history.push("/expired");
            break;
          default:
            console.error(response);
            const errorDetails = await response.json();
            faro.api.pushError(new Error("Bank Update Error"), {
              context: errorDetails,
            });
            history.push({
              pathname: "/error",
              state: {
                message:
                  errorDetails.patronErrorMessage ??
                  "Error Updating Funding Source",
                showButton: true,
                allowRetry: false,
                customButtons: customErrorButtons,
                transactionInitiated: false,
              },
            });
        }
      }
    }
  }

  let defaultAccountSet =
    !fundingSources?.filter(
      (account) =>
        account.defaultAccountNumber === "True" &&
        session.transactionAmount > account.availableLimit,
    ).length > 0;

  const fundList = fundingSources?.map((account, index) => {
    const isOnline = session?.patronProductType === PRODUCTTYPES.ONLINE;
    const accountAvailable =
      (isOnline &&
        isDepositMode &&
        sessionStorageTransAmount < account.availableLimit) ||
      !isOnline ||
      !isDepositMode;
    let accountIsDefault = false;
    accountIsDefault = account.defaultAccountNumber === "True";
    // for VIPO only change default account if default has insufficient funds
    if (isOnline && !defaultAccountSet) {
      if (account.defaultAccountNumber === "True" && accountAvailable) {
        accountIsDefault = true;
        defaultAccountSet = true;
      } else if (account.defaultAccountNumber === "True" && !accountAvailable) {
        accountIsDefault = false;
        defaultAccountSet = false;
      } else if (accountAvailable) {
        accountIsDefault = true;
        defaultAccountSet = true;
        updateAccount(account);
      } else {
        accountIsDefault = false;
      }
    }
    return (
      <Box
        key={index}
        sx={{
          borderBottom:
            index < fundingSources.length - 1
              ? "1px solid rgba(139, 139, 139, 0.2)"
              : undefined,
          display: "flex",
          flexDirection: "row",
          padding:
            "var(--spacing-md, 12px) var(--spacing-2xl, 24px) var(--spacing-lg, 16px) var(--spacing-lg, 16px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            marginRight: "16px",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <Radio
            disabled={!accountAvailable}
            value={account.accountNumber}
            className="click-set-default-account"
            checked={
              userSelection
                ? userSelection === account.accountNumber
                : accountIsDefault
            }
            onChange={() => updateAccount(account)}
            sx={{
              padding: 0,
              color: "var(--color-secondary-gray-tint-40)",
              "&.Mui-checked": {
                color: "var(--color-primary-lime-green-50)",
              },
              "& .MuiSvgIcon-root": {
                fontSize: 24,
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: "1",
            justifyContent: "center",
          }}
        >
          <Box className="bank-name">{account.bankName}</Box>
          <Box className="account-number">
            *****{account.accountNumberDisplay}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteAccountButton
            account={account}
            bankAccountData={fundingSources}
          />
        </Box>
        {!accountAvailable && (
          <Box>
            <Box>
              <img
                height="20"
                alt="Insufficient Funds in Account"
                src={errorIcon}
              />
            </Box>
            <Box>
              This account has insufficient funds. Bank accounts must have a
              higher balance than your deposit amount to complete a transaction
              with VIP Online.
            </Box>
          </Box>
        )}
      </Box>
    );
  });

  const onClickAdd = () => {
    history.push({ pathname: "/addAccount", state: { notRedirect: true } });
    faro.api.pushEvent(
      "User clicked to Add Account from My Funding Sources page",
    );
  };

  let button = (
    <Button
      fullWidth
      className="submit-button click-add-account"
      onClick={onClickAdd}
      padding={mode == MODE.manageBanks ? undefined : "var(--spacing-lg)"}
    >
      Add Account
    </Button>
  );

  const tooManyBanks =
    fundType === FUNDTYPE.netspend
      ? bankAccountList?.length >= 3
      : bankAccountList?.length >= 4;
  if (tooManyBanks) {
    button = (
      <button className="submit-button disabled">Cannot Add Account</button>
    );
  }

  return (
    <Box className="bank-list">
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.20)",
          background: "var(--color-surface-background-primary, #FFF)",
          marginBottom: "32px",
        }}
      >
        {fundList}
      </Box>
      {!tooManyBanks && button}
      {tooManyBanks && (
        <div className="invalid-feedback d-block text-center">
          {fundType === FUNDTYPE.netspend
            ? "Only 3 Prepaid accounts allowed. You may remove a Prepaid account to add a new one."
            : "Only 4 bank accounts allowed. You may remove a bank account to add a new one."}
        </div>
      )}
      {mode === MODE.manageBanks && customButtonsComponents}
    </Box>
  );
};

export default BankList;
